/* ============================================================
   OptiMind site-wide motion layer  (non-homepage templates)
   - Scroll reveal via IntersectionObserver (staggered per group)
   - Count-up for [data-countup]
   - Lightweight parallax for [data-parallax]
   - Auto-tags common content elements so existing pages animate
     without per-section markup edits.
   Fully no-ops under prefers-reduced-motion.

   UX-AUDIT FIX (2026-06-04):
   1. Adds html.reveal-on BEFORE hiding anything, so reveal.css only
      hides content while JS is active. If this script fails to load,
      content is never hidden (no more invisible pages).
   2. Immediately reveals elements already in the viewport at load —
      fixes above-the-fold H1 / hero copy that previously stayed at
      opacity:0 because the first IntersectionObserver callback was
      missed for already-visible elements.
   3. Hard failsafe: any element still hidden after 1600ms is revealed.
   ============================================================ */
(function () {
  'use strict';
  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var docEl = document.documentElement;
  var main = document.getElementById('main-content') || document.querySelector('main') || document.body;
  if (!main) return;

  /* Gate the hidden state behind this class (see reveal.css).
     Under reduced motion we never hide, so we never add it. */
  if (!REDUCE) docEl.classList.add('reveal-on');

  /* ---------- 1. AUTO-TAG candidate elements for reveal ---------- */
  var AUTO = [
    'h1', 'h2', 'h3',
    'p', 'blockquote',
    'img', 'picture', 'svg.illustration',
    '.card', '.product-card', '.blog-card', '.article-card', '.collection-card',
    '.feature', '.benefit-card', '.faq-item', '.testimonial',
    'li.reveal-li', '.media', 'figure',
    '.rte > p', '.rte > ul', '.rte > ol', '.rte > h2', '.rte > h3',
    '.section-header', '.title-wrapper', '.banner__content'
  ];

  function shouldTag(el) {
    if (el.hasAttribute('data-reveal') || el.hasAttribute('data-no-reveal')) return false;
    if (el.closest('[data-no-reveal]')) return false;
    if (el.closest('nav, header, footer')) return false;
    return true;
  }

  if (!REDUCE) {
    try {
      var nodes = main.querySelectorAll(AUTO.join(','));
      nodes.forEach(function (el) {
        if (shouldTag(el)) el.setAttribute('data-reveal', '');
      });
    } catch (e) { /* selector engine edge cases — ignore */ }
  }

  /* ---------- 2. Stagger: index siblings within their parent ---------- */
  var revealEls = Array.prototype.slice.call(main.querySelectorAll('[data-reveal]'));

  function reveal(el) { el.classList.add('is-in'); }
  function inView(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || docEl.clientHeight;
    return r.top < vh && r.bottom > 0;
  }

  if (REDUCE) {
    revealEls.forEach(reveal);
  } else {
    var seen = new Map();
    revealEls.forEach(function (el) {
      var p = el.parentNode;
      var i = seen.get(p) || 0;
      seen.set(p, i + 1);
      var delay = Math.min(i * 70, 420);
      el.style.setProperty('--reveal-delay', delay + 'ms');
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
      revealEls.forEach(function (el) { io.observe(el); });

      /* FIX 2: reveal anything already on-screen at load. The initial
         IntersectionObserver callback is unreliable for elements that
         are already in the viewport, which left above-the-fold content
         (product H1, hero copy) permanently invisible. */
      requestAnimationFrame(function () {
        revealEls.forEach(function (el) {
          if (!el.classList.contains('is-in') && inView(el)) {
            reveal(el);
            io.unobserve(el);
          }
        });
      });

      /* FIX 3: failsafe — never let content stay hidden. */
      setTimeout(function () {
        revealEls.forEach(function (el) {
          if (!el.classList.contains('is-in')) reveal(el);
        });
      }, 1600);
    } else {
      revealEls.forEach(reveal);
    }
  }

  /* ---------- 3. Count-up numbers ---------- */
  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-countup'));
    if (isNaN(target)) return;
    var decimals = (el.getAttribute('data-countup-decimals') | 0);
    var prefix = el.getAttribute('data-countup-prefix') || '';
    var suffix = el.getAttribute('data-countup-suffix') || '';
    if (REDUCE) { el.textContent = prefix + target.toFixed(decimals) + suffix; return; }
    var start = null, dur = 1300;
    function ease(p) { return p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; }
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      el.textContent = prefix + (target * ease(p)).toFixed(decimals) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  var counters = main.querySelectorAll('[data-countup]');
  if (counters.length) {
    if ('IntersectionObserver' in window && !REDUCE) {
      var cio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { countUp(entry.target); cio.unobserve(entry.target); }
        });
      }, { threshold: 0.5 });
      counters.forEach(function (el) { cio.observe(el); });
    } else {
      counters.forEach(countUp);
    }
  }

  /* ---------- 4. Lightweight parallax ---------- */
  if (!REDUCE) {
    var pels = Array.prototype.slice.call(main.querySelectorAll('[data-parallax]'));
    if (pels.length) {
      var ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var vh = window.innerHeight;
          pels.forEach(function (el) {
            var r = el.getBoundingClientRect();
            if (r.bottom < 0 || r.top > vh) return;
            var speed = parseFloat(el.getAttribute('data-parallax')) || 0.15;
            var offset = (r.top + r.height / 2 - vh / 2) * -speed;
            el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
          });
          ticking = false;
        });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      onScroll();
    }
  }
})();
